import {TranslationObject} from '@colonist/i18n'
import {UIAPIEndpoints} from '../../network/UIAPIEndpoints'
import {UIAjaxWrapper} from '../../network/UIAjaxWrapper'
import {ViewPopup} from '../ViewPopup'
import {ViewPopupOptions} from '../ViewPopupOptions'
import {notificationController} from '../../EntryShared'
import {LobbyImage} from '@colonist/types'
import {TimeUtils} from '@colonist/utils'
import {NotificationData, NotificationTypeEnum} from '../../../../../shared/NotificationTypes'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {ClientUserState} from '../../ClientUserState'
import {navigateToPage} from '../../ui_utils/ClientUtils.ts'

interface ViewPopupData {
    title: TranslationObject
    body: TranslationObject
    popupOptions?: ViewPopupOptions
    checkAction?: () => void
}

interface CornerPopupData {
    image: LobbyImage
    text: TranslationObject
    duration?: number
}

export class UIServerNotificationController {
    static async displayAllPendingNotifications() {
        if(UIUtilsEjsVariables.isUserOnDiscord) return
        if(UIUtilsEjsVariables.isUserOnMobileApp) return
        if(ClientUserState.userState == undefined) return

        const notificationPanelContainer = document.getElementById('header_profile_notification_container') as HTMLElement
        const isNotificationPanelVisible = notificationPanelContainer.classList.contains('hidden') == false
        if(isNotificationPanelVisible) return

        const res = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiGetNotifications(), undefined)
        if(res == undefined) return
        const notifications = res.json as NotificationData[]

        for(const notification of notifications) {
            if(this.isCornerPopup(notification)) {
                this.displayCornerPopup(notification)
            } else {
                this.displayViewPopup(notification)
            }
        }
    }

    private static isCornerPopup(notification: NotificationData): boolean {
        switch(notification.typeData.type) {
            case NotificationTypeEnum.UserReportedWarning:
                return true
            default:
                return false
        }
    }

    private static displayCornerPopup(notification: NotificationData) {
        const popupData = this.getCornerPopupData(notification)
        notificationController.showNotification(popupData.image, popupData.text, popupData.duration, undefined, this.markNotificationAsReadAction(notification.id))
    }

    private static displayViewPopup(notification: NotificationData) {
        const popupData = this.getViewPopupData(notification)
        if(popupData == undefined) return
        const popupCheckAction = popupData.checkAction ?? (() => undefined)
        const popup = new ViewPopup(popupData.title, popupData.body, popupCheckAction, () => undefined, true, popupData.popupOptions)
        popup.removeXButton()
        popup.removeAction = this.markNotificationAsReadAction(notification.id)
        popup.show()
    }

    private static getViewPopupData(notification: NotificationData): ViewPopupData | undefined {
        switch(notification.typeData.type) {
            case NotificationTypeEnum.UserReportedBanned:
                return {
                    title: {key: 'strings:popups.notification.reportedUserBanned.title'},
                    body: {key: 'strings:popups.notification.reportedUserBanned.body'},
                }
            case NotificationTypeEnum.UserReportedThankYou:
                return {
                    title: {key: 'strings:popups.notification.reportedUserThankYou.title'},
                    body: {key: 'strings:popups.notification.reportedUserThankYou.body'},
                }
            case NotificationTypeEnum.HolidaySale2023Expired:
                return {
                    title: {key: 'strings:popups.notification.holidaySale2023Expired.title'},
                    body: {key: 'strings:popups.notification.holidaySale2023Expired.body'},
                }
            case NotificationTypeEnum.TranslationHelp:
                return {
                    title: {key: 'strings:popups.notification.translationHelp.title'},
                    body: {
                        key: 'strings:popups.notification.translationHelp.body',
                        options: {
                            language: notification.typeData.language,
                            username: notification.typeData.username,
                            link: notification.typeData.link,
                        },
                    },
                }
            case NotificationTypeEnum.GiftMembershipReceivedFromAdmin:
                return {
                    title: {key: 'strings:popups.notification.giftMembershipReceived.title'},
                    body: {key: 'strings:popups.notification.giftMembershipReceived.body'},
                    popupOptions: {checkButtonCustomContent: {key: 'strings:popups.notification.giftMembershipReceived.checkButton'}},
                    checkAction: () => navigateToPage('/store#gifted_memberships', true),
                }
            default:
                return undefined
        }
    }

    private static getCornerPopupData(notification: NotificationData): CornerPopupData {
        switch(notification.typeData.type) {
            case NotificationTypeEnum.UserReportedWarning:
                return {
                    image: LobbyImage.DangerIcon,
                    text: {key: 'strings:popups.notification.reportedUserWarning'},
                    duration: TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MILLISECONDS,
                }
            default:
                throw new Error('Unsupported notification type for displayCornerPopup')
        }
    }

    private static markNotificationAsReadAction(notificationId: string, additionalRemoveAction?: () => void) {
        return async() => {
            await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiMarkNotificationAsRead(notificationId), undefined, undefined)
            additionalRemoveAction?.()
        }
    }
}
